import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";

const adminApiUrl =
  process.env.REACT_APP_ADMIN_API_URL || "http://localhost:4001";

/**
 * Subscribe to realtime contest updates (entries + leaderboard count) over WebSocket.
 * No infra changes — connects to the admin service /live-contest namespace.
 */
export function useLiveContestRealtime({
  productId,
  playToken,
  enabled = true,
  onUpdate,
}) {
  const [connected, setConnected] = useState(false);
  const onUpdateRef = useRef(onUpdate);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    if (!enabled || !productId || !playToken) {
      setConnected(false);
      return undefined;
    }

    const socket = io(`${adminApiUrl}/live-contest`, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
    });

    const subscribe = () => {
      socket.emit("subscribe", { productId, playToken });
    };

    socket.on("connect", () => {
      setConnected(true);
      subscribe();
    });

    socket.on("disconnect", () => {
      setConnected(false);
    });

    socket.on("contest.update", (payload) => {
      onUpdateRef.current?.(payload);
    });

    if (socket.connected) {
      subscribe();
    }

    return () => {
      socket.emit("unsubscribe", { productId, playToken });
      socket.disconnect();
      setConnected(false);
    };
  }, [enabled, productId, playToken]);

  return { connected };
}

export default useLiveContestRealtime;
